import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import serviceApi from '../services/serviceApi';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import { formatSAR } from '../utils/currencyFormatter';
import { PAYMENT_METHOD_LABELS } from '../config/serviceConstants';
import InvoiceStatusBadge from '../components/invoices/InvoiceStatusBadge';
import RecordPaymentModal from '../components/invoices/RecordPaymentModal';
import RecordRefundModal from '../components/invoices/RecordRefundModal';
import CancelInvoiceModal from '../components/invoices/CancelInvoiceModal';
import '../styles/Invoices.css';

const SERVICE_TYPE_LABELS = {
  basic: 'تنظيف عادي',
  deep: 'تنظيف عميق'
};

const SIZE_TIER_LABELS = {
  studio: 'استوديو',
  '1br':  'غرفة واحدة',
  '2br':  'غرفتين',
  '3br':  'ثلاث غرف',
  '4br':  'أربع غرف',
  '5br+': 'خمس غرف فأكثر'
};

const TIMELINE_EVENT_LABELS = {
  issued:           'تم إصدار الفاتورة',
  payment_recorded: 'تم تسجيل دفعة',
  refund_recorded:  'تم تسجيل استرداد',
  cancelled:        'تم إلغاء الفاتورة'
};

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [activeNavItem, setActiveNavItem] = useState('services');

  const [showPayment, setShowPayment] = useState(false);
  const [showRefund, setShowRefund]   = useState(false);
  const [showCancel, setShowCancel]   = useState(false);

  const fetchInvoice = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await serviceApi.invoices.get(id);
      setInvoice(res.data?.invoice || null);
      // Server-provided form defaults (business date, Riyadh-resolved). Computing
      // these in the browser returns the UTC day and pre-fills yesterday between
      // 00:00 and 03:00 Riyadh — on the 1st, into the previous month.
      setDefaults(res.data?.defaults || null);
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError('تعذر تحميل بيانات الفاتورة');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  if (loading) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem} headerTitle="الفاتورة">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  if (error || !invoice) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem} headerTitle="الفاتورة">
        <div className="inv-detail" dir="rtl">
          <button className="inv-detail__back" onClick={() => navigate('/services/invoices')}>
            <i className="bi bi-arrow-right" /> رجوع للقائمة
          </button>
          <div className="svc-clients__empty">
            <div className="svc-clients__empty-icon"><i className="bi bi-exclamation-triangle" /></div>
            <p className="svc-clients__empty-text">{error || 'الفاتورة غير موجودة'}</p>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  const c  = invoice.commercials || {};
  const ps = invoice.packageSnapshot || {};
  const cs = invoice.subscriptionCycleSnapshot;
  const refunds = invoice.refunds || [];
  const refundsTotal = refunds.reduce((sum, r) => sum + (r.amount || 0), 0);
  const refundCap = Math.max(0, (c.totalAmount || 0) - refundsTotal);
  const isSubscription = !!invoice.subscriptionId;
  const clientUser = invoice.clientId?.userId;

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="الفاتورة"
    >
      <div className="inv-detail" dir="rtl">

        <button className="inv-detail__back" onClick={() => navigate('/services/invoices')}>
          <i className="bi bi-arrow-right" /> رجوع لقائمة الفواتير
        </button>

        {/* Header */}
        <div className="inv-detail__header">
          <div className="inv-detail__header-left">
            <h1>{invoice.invoiceNumber}</h1>
            <div className="inv-detail__client-name">
              {clientUser?.name || '—'}
              {clientUser?.clientCode && (
                <span style={{ color: '#6c757d', fontWeight: 400, marginRight: 8 }}>
                  ({clientUser.clientCode})
                </span>
              )}
            </div>
            {(clientUser?.email || clientUser?.phone) && (
              <div className="inv-detail__client-meta">
                {clientUser.email}{clientUser.phone ? ` • ${clientUser.phone}` : ''}
              </div>
            )}
            <div className="inv-detail__date">
              تاريخ الإصدار: {formatDate(invoice.issueDate)}
            </div>
          </div>
          <div>
            <InvoiceStatusBadge status={invoice.status} refundsCount={refunds.length} />
          </div>
        </div>

        <div className="inv-detail__grid">

          {/* Left column — financial breakdown + snapshots */}
          <div>
            {/* Commercials */}
            <div className="inv-section">
              <h3>تفاصيل المبلغ</h3>
              <ul className="inv-commercials">
                <li>
                  <span>السعر قبل الخصم</span>
                  <span dir="ltr">{formatSAR(c.listPrice)}</span>
                </li>
                <li>
                  <span>الخصم</span>
                  <span dir="ltr">−{formatSAR(c.discountAmount)}</span>
                </li>
                <li>
                  <span>الصافي</span>
                  <span dir="ltr">{formatSAR(c.netAmount)}</span>
                </li>
                <li>
                  <span>ضريبة القيمة المضافة ({c.vatRate ?? 15}%)</span>
                  <span dir="ltr">{formatSAR(c.vatAmount)}</span>
                </li>
                <li className="inv-commercials--total">
                  <span>الإجمالي</span>
                  <span dir="ltr">{formatSAR(c.totalAmount)}</span>
                </li>
              </ul>
            </div>

            {/* Package snapshot */}
            <div className="inv-section">
              <h3>تفاصيل الباقة</h3>
              <dl className="inv-snapshot-grid">
                <dt>اسم الباقة</dt>
                <dd>{ps.packageName || '—'}</dd>
                <dt>نوع الخدمة</dt>
                <dd>{SERVICE_TYPE_LABELS[ps.serviceType] || ps.serviceType || '—'}</dd>
                <dt>الفئة</dt>
                <dd>{SIZE_TIER_LABELS[ps.sizeTier] || ps.sizeTier || '—'}</dd>
                {ps.packageCode && (
                  <>
                    <dt>كود الباقة</dt>
                    <dd style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{ps.packageCode}</dd>
                  </>
                )}
                {isSubscription && (
                  <>
                    <dt>عدد الزيارات</dt>
                    <dd>{ps.visitsIncluded ?? '—'}</dd>
                    <dt>مدة الصلاحية</dt>
                    <dd>{ps.validityDays ? `${ps.validityDays} يوم` : '—'}</dd>
                  </>
                )}
              </dl>
            </div>

            {/* Cycle snapshot — sub only */}
            {isSubscription && cs && (
              <div className="inv-section">
                <h3>دورة الاشتراك</h3>
                <dl className="inv-snapshot-grid">
                  <dt>بداية الدورة</dt>
                  <dd>{formatDate(cs.billingCycleStart)}</dd>
                  <dt>نهاية الدورة</dt>
                  <dd>{formatDate(cs.billingCycleEnd)}</dd>
                  <dt>زيارات الدورة</dt>
                  <dd>{cs.visitsIncluded ?? '—'}</dd>
                </dl>
              </div>
            )}

            {/* Source linkage */}
            <div className="inv-section">
              <h3>المصدر</h3>
              {isSubscription ? (
                <div style={{ fontSize: '0.92rem', color: '#495057' }}>
                  <i className="bi bi-arrow-repeat me-2" style={{ color: '#471396' }} />
                  فاتورة اشتراك
                  {invoice.clientId?._id && (
                    <button
                      className="inv-source-link"
                      style={{ background: 'none', border: 'none', padding: 0, marginRight: 12, cursor: 'pointer' }}
                      onClick={() => navigate(`/services/clients/${invoice.clientId._id}`)}
                    >
                      <i className="bi bi-box-arrow-in-left" />
                      عرض العميل والاشتراك
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '0.92rem', color: '#495057' }}>
                  <i className="bi bi-calendar-check me-2" style={{ color: '#1565c0' }} />
                  فاتورة زيارة فردية
                  {invoice.clientId?._id && (
                    <button
                      className="inv-source-link"
                      style={{ background: 'none', border: 'none', padding: 0, marginRight: 12, cursor: 'pointer' }}
                      onClick={() => navigate(`/services/clients/${invoice.clientId._id}`)}
                    >
                      <i className="bi bi-box-arrow-in-left" />
                      عرض العميل والطلب
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Refunds list */}
            {refunds.length > 0 && (
              <div className="inv-section">
                <h3>الاستردادات ({refunds.length})</h3>
                <ul className="inv-refunds">
                  {refunds.map((r, idx) => (
                    <li key={r._id || idx}>
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {formatSAR(r.amount)}
                          <span style={{ color: '#6c757d', fontWeight: 400, fontSize: '0.78rem', marginRight: 8 }}>
                            • {PAYMENT_METHOD_LABELS[r.method] || r.method}
                          </span>
                        </div>
                        {r.note && (
                          <div style={{ color: '#6c757d', fontSize: '0.82rem', marginTop: 2 }}>{r.note}</div>
                        )}
                      </div>
                      <span style={{ color: '#6c757d', fontSize: '0.78rem' }}>
                        {r.refundedAt ? formatDate(r.refundedAt) : '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right column — actions + timeline */}
          <div>
            {/* Actions panel */}
            <div className="inv-section">
              <h3>الإجراءات</h3>
              <div className="inv-actions">
                {invoice.status === 'issued' && (
                  <>
                    <button
                      className="inv-action-btn inv-action-btn--primary"
                      onClick={() => setShowPayment(true)}
                    >
                      <i className="bi bi-cash-stack me-2" /> تسجيل دفعة
                    </button>
                    <button
                      className="inv-action-btn inv-action-btn--danger"
                      onClick={() => setShowCancel(true)}
                    >
                      <i className="bi bi-x-circle me-2" /> إلغاء الفاتورة
                    </button>
                  </>
                )}
                {invoice.status === 'paid' && (
                  <>
                    <button
                      className="inv-action-btn inv-action-btn--warn"
                      onClick={() => setShowRefund(true)}
                      disabled={refundCap <= 0}
                    >
                      <i className="bi bi-arrow-counterclockwise me-2" /> تسجيل استرداد
                    </button>
                    {refundCap <= 0 && (
                      <p className="inv-actions__hint">
                        تم استرداد كامل المبلغ
                      </p>
                    )}
                    <p className="inv-actions__hint">
                      الفواتير المدفوعة لا تُلغى — يتم الاسترداد فقط
                    </p>
                  </>
                )}
                {invoice.status === 'cancelled' && (
                  <p className="inv-actions__hint">
                    هذه الفاتورة ملغاة — لا توجد إجراءات متاحة
                  </p>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="inv-section">
              <h3>سجل الفاتورة</h3>
              {(!invoice.statusHistory || invoice.statusHistory.length === 0) ? (
                <p style={{ color: '#6c757d', fontSize: '0.85rem', margin: 0 }}>لا توجد أحداث</p>
              ) : (
                <ul className="inv-timeline">
                  {(() => {
                    // Pre-build event-amount map so each timeline entry can show
                    // the SAR figure inline (consultant guidance — concise summary,
                    // not full duplication). payment_recorded uses payments[0]
                    // (decision #22 = 1 payment max). refund_recorded events are
                    // matched ordinally to refunds[] by chronological occurrence.
                    let refundIdx = 0;
                    return invoice.statusHistory.map((ev, idx) => {
                      let inlineAmount = null;
                      if (ev.event === 'payment_recorded') {
                        const p = (invoice.payments || [])[0];
                        if (p?.amount != null) inlineAmount = formatSAR(p.amount);
                      } else if (ev.event === 'refund_recorded') {
                        const r = (invoice.refunds || [])[refundIdx++];
                        if (r?.amount != null) inlineAmount = `−${formatSAR(r.amount)}`;
                      }
                      return (
                        <li key={ev._id || idx}>
                          <div className="inv-timeline__event">
                            {TIMELINE_EVENT_LABELS[ev.event] || ev.event}
                            {inlineAmount && (
                              <span
                                style={{ color: '#471396', fontWeight: 700, marginRight: 8 }}
                                dir="ltr"
                              >
                                — {inlineAmount}
                              </span>
                            )}
                          </div>
                          <div className="inv-timeline__meta">
                            {ev.at ? formatDateTime(ev.at) : '—'}
                            {ev.by?.name && <span> • {ev.by.name}</span>}
                          </div>
                          {ev.note && (
                            <div className="inv-timeline__note">{ev.note}</div>
                          )}
                        </li>
                      );
                    });
                  })()}
                </ul>
              )}
            </div>
          </div>

        </div>
      </div>

      <RecordPaymentModal
        show={showPayment}
        onHide={() => setShowPayment(false)}
        invoice={invoice}
        defaultPaidAt={defaults?.paidAt}
        onRecorded={fetchInvoice}
      />
      <RecordRefundModal
        show={showRefund}
        onHide={() => setShowRefund(false)}
        invoice={invoice}
        defaultRefundedAt={defaults?.refundedAt}
        onRecorded={fetchInvoice}
      />
      <CancelInvoiceModal
        show={showCancel}
        onHide={() => setShowCancel(false)}
        invoice={invoice}
        onCancelled={fetchInvoice}
      />
    </DesktopLayout>
  );
};

export default InvoiceDetailPage;
